import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslations } from "use-intl";

import { Link } from "@/components/link";

// Used when the error happens above the root's IntlProvider (e.g. inside
// `__root.beforeLoad` itself), so `useTranslations` has no context to read.
const FALLBACK_STRINGS = {
  title: "A Agulha Pulou",
  message:
    "Algo deu errado ao carregar esta página. O disco pode estar arranhado — tente dar mais uma girada.",
  tryAgain: "Tentar novamente",
  backHome: "Voltar a garimpar",
};

function useErrorStrings() {
  try {
    const t = useTranslations("globals.error");
    return {
      title: t("title"),
      message: t("message"),
      tryAgain: t("tryAgain"),
      backHome: t("backHome"),
    };
  } catch {
    return FALLBACK_STRINGS;
  }
}

export function AppError({ error, reset }: ErrorComponentProps) {
  // Server errors are logged through the router's server-only defaultOnCatch
  // handler. Keep browser failures visible in the developer console.
  useEffect(() => {
    console.error(error);
  }, [error]);

  const strings = useErrorStrings();

  // Fixed-overlay layout: visually replaces whatever parent layout wrapped the
  // boundary (Header / Footer / BottomNav from `_app`, etc.), so the error
  // page always shows just the logo bar and the centered content regardless
  // of which route caught the throw.
  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-background">
      <header className="absolute left-0 right-0 top-0 px-4 md:px-10 lg:px-20 py-3">
        <a
          aria-label="Garimpa.Club home"
          href="/"
          className="flex min-h-11 items-center gap-3 text-primary"
        >
          <svg
            className="size-8"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <mask id="garimpa-logo-error">
                <circle cx="24" cy="24" r="22" fill="white" />
                <circle cx="24" cy="24" r="17" fill="none" stroke="black" strokeWidth="1.2" />
                <circle cx="24" cy="24" r="12" fill="none" stroke="black" strokeWidth="1.2" />
                <polygon points="24,19 29,24 24,29 19,24" fill="black" />
              </mask>
            </defs>
            <circle cx="24" cy="24" r="22" fill="currentColor" mask="url(#garimpa-logo-error)" />
          </svg>
          <h2 className="hidden text-xl font-bold leading-tight tracking-tight sm:block">
            Garimpa.Club
          </h2>
        </a>
      </header>

      <main className="flex min-h-full flex-col items-center justify-center px-4 text-center">
        <svg
          className="mb-8"
          width="140"
          height="140"
          viewBox="0 0 140 140"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            cx="70"
            cy="70"
            r="68"
            stroke="hsl(var(--border))"
            strokeWidth="2"
            fill="hsl(var(--secondary))"
          />
          <circle
            cx="70"
            cy="70"
            r="58"
            stroke="hsl(var(--border))"
            strokeWidth="0.5"
            fill="none"
          />
          <circle
            cx="70"
            cy="70"
            r="50"
            stroke="hsl(var(--border))"
            strokeWidth="0.5"
            fill="none"
          />
          <circle
            cx="70"
            cy="70"
            r="42"
            stroke="hsl(var(--border))"
            strokeWidth="0.5"
            fill="none"
          />
          <circle
            cx="70"
            cy="70"
            r="34"
            stroke="hsl(var(--border))"
            strokeWidth="0.5"
            fill="none"
          />
          <line
            x1="45"
            y1="30"
            x2="85"
            y2="110"
            stroke="hsl(var(--destructive))"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <line
            x1="85"
            y1="110"
            x2="75"
            y2="95"
            stroke="hsl(var(--destructive))"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <circle cx="70" cy="70" r="22" fill="hsl(var(--destructive))" />
          <circle cx="70" cy="70" r="3.5" fill="hsl(var(--background))" />
          <text
            x="70"
            y="75"
            textAnchor="middle"
            fill="hsl(var(--background))"
            fontSize="24"
            fontWeight="700"
            fontFamily="Space Grotesk, system-ui, sans-serif"
          >
            !
          </text>
        </svg>

        <h1 className="mb-3 text-xl font-semibold">{strings.title}</h1>
        <p className="mb-8 max-w-md text-base text-muted-foreground">{strings.message}</p>
        <div className="flex gap-3">
          <button
            onClick={reset}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {strings.tryAgain}
          </button>
          <Link
            to="/"
            className="rounded-lg border border-border px-6 py-2.5 text-sm font-medium transition-colors hover:bg-secondary"
          >
            {strings.backHome}
          </Link>
        </div>
      </main>
    </div>
  );
}
